import { useState } from 'react';
import {
  adminUserUpdate,
  customRoleCreate,
  customRoleDelete,
  customRolesList,
  householdCreate,
  type ApiError,
  type CustomRole,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { CAPT, ErrLine, I, btn, pre, sx, useApi, useGuarded } from './kit';

// Restored from the Flutter Facilities page (facilities_view.dart): a building's custom
// roles (a base tier plus a bounded set of extra capabilities) with create, delete and
// assign, and the operator's "create an Aithora Home household". Drawn in the section's
// own style, on Users & Permissions; no new page.

const INPUT = 'flex:1 1 160px;min-width:0;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;';
const ROW = 'display:flex;align-items:center;gap:8px 12px;flex-wrap:wrap;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;';
const TIER: Record<string, string> = { CAREGIVER: 'Care Staff', STAFF: 'Staff', ADMIN: 'Facility Admin' };
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** One building's custom roles: the list, + New role, and Delete (confirm-first). */
export function CustomRolesPanel({ orgId, orgName }: { orgId: string; orgName: string }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const q = useApi((c, s) => customRolesList(c, { params: { orgId }, signal: s }), [orgId]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [tier, setTier] = useState('CAREGIVER');
  const [caps, setCaps] = useState<string[]>([]);
  const [err, setErr] = useState<ApiError | null>(null);
  const [del, setDel] = useState<CustomRole | null>(null);
  const roles = q.data?.roles ?? [];
  const capLabel = (id: string) => q.data?.extraCaps.find((c) => c.id === id)?.label ?? id;

  const create = async () => {
    if (!name.trim()) return;
    setErr(null);
    try {
      await customRoleCreate(client, { params: { orgId }, body: { name: name.trim(), baseRole: tier, addedCaps: caps } });
      setName('');
      setCaps([]);
      setOpen(false);
      q.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  return (
    <div data-testid={`custom-roles-${orgId}`} style={sx('margin-top:8px;min-width:0;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;')}>
        <span style={sx(CAPT)}>{I('Custom roles · ' + orgName)}</span>
        <span style={sx('flex:1;')} />
        {!open && (
          <a href="#" {...wr(() => setOpen(true))} style={sx(btn('ghost'))}>
            + New role
          </a>
        )}
      </div>
      {q.error && <ErrLine error={q.error} />}
      {roles.length === 0 && !q.error && !open && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;')}>No custom roles yet. Everyone holds a standard role.</div>}
      {roles.map((r) => (
        <div key={r.id} style={sx(ROW)}>
          <span style={sx('flex:1 1 200px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(r.name)}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>
              {I([TIER[r.base_role] ?? r.base_role, ...(r.added_caps ?? []).map(capLabel)].join(' · '))}
            </span>
          </span>
          <a href="#" {...wr(() => setDel(r))} style={sx(btn('ghost') + 'color:#B3261E;')}>
            Delete
          </a>
        </div>
      ))}
      {open && (
        <div style={sx('display:flex;flex-direction:column;gap:8px;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
            <input type="text" aria-label="Role name" placeholder="Role name, e.g. Night nurse" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} style={sx(INPUT)} />
            <select aria-label="Base role" value={tier} onChange={(e) => setTier(e.target.value)} style={sx(INPUT + 'flex:0 0 auto;')}>
              {(q.data?.baseTiers ?? ['CAREGIVER', 'STAFF', 'ADMIN']).map((t) => (
                <option key={t} value={t}>
                  {TIER[t] ?? t}
                </option>
              ))}
            </select>
          </div>
          <div style={sx('display:flex;gap:6px 14px;flex-wrap:wrap;')}>
            {(q.data?.extraCaps ?? []).map((c) => (
              <label key={c.id} style={sx('display:inline-flex;align-items:center;gap:6px;font-size:16px;color:#22272E;cursor:pointer;')}>
                <input type="checkbox" checked={caps.includes(c.id)} onChange={(e) => setCaps(e.target.checked ? [...caps, c.id] : caps.filter((x) => x !== c.id))} />
                {I(c.label)}
              </label>
            ))}
          </div>
          <div style={sx('display:flex;gap:8px;')}>
            <a href="#" {...wr(() => void create())} aria-disabled={!name.trim() || undefined} style={sx(btn(name.trim() ? 'primary' : 'off'))}>
              Create role
            </a>
            <a href="#" onClick={(e) => { pre(e); setOpen(false); }} style={sx(btn('ghost'))}>
              Cancel
            </a>
          </div>
        </div>
      )}
      <ErrLine error={err} />
      {del && (
        <ConfirmDialog
          title={`Delete the ${del.name} role?`}
          body="Anyone who holds it keeps their standard role and loses the extra permissions."
          confirmLabel="Delete"
          danger
          onConfirm={async () => {
            await customRoleDelete(client, { params: { orgId, roleId: del.id } });
            q.reload();
          }}
          onClose={() => setDel(null)}
        />
      )}
    </div>
  );
}

/** Give one person a custom role of their building, or take it away. */
export function CustomRolePicker({ userId, orgId, current, onChanged }: { userId: string; orgId: string; current?: string | null; onChanged: () => void }) {
  const { client } = useSession();
  const { canWrite, roReason } = useGuarded();
  const q = useApi((c, s) => customRolesList(c, { params: { orgId }, signal: s }), [orgId]);
  const [err, setErr] = useState<ApiError | null>(null);
  const roles = q.data?.roles ?? [];
  if (!roles.length) return null;
  return (
    <>
      <select
        aria-label="Custom role"
        disabled={!canWrite}
        title={roReason ?? undefined}
        value={current ?? ''}
        onChange={async (e) => {
          setErr(null);
          try {
            await adminUserUpdate(client, { params: { id: userId }, body: { customRoleId: e.target.value || null } });
            onChanged();
          } catch (x) {
            setErr(x as ApiError);
          }
        }}
        style={sx(INPUT + 'flex:0 0 auto;height:30px;')}
      >
        <option value="">No custom role</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      <ErrLine error={err} style="flex-basis:100%;margin-top:4px;" />
    </>
  );
}

/** Aithora creates an Aithora Home household: the senior's account and a family invite. */
export function HouseholdCreate() {
  const { client } = useSession();
  const { wr } = useGuarded();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ householdName: '', seniorName: '', seniorEmail: '', familyName: '', familyEmail: '' });
  const [err, setErr] = useState<ApiError | null>(null);
  const [made, setMade] = useState<{ url: string | null; seniorUrl: string | null } | null>(null);
  const ok = f.householdName.trim() && f.seniorName.trim() && (!f.seniorEmail.trim() || EMAIL.test(f.seniorEmail.trim())) && (!f.familyEmail.trim() || EMAIL.test(f.familyEmail.trim()));
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const create = async () => {
    if (!ok) return;
    setErr(null);
    try {
      const trim = (v: string) => v.trim() || undefined;
      const r = await householdCreate(client, {
        body: { householdName: f.householdName.trim(), seniorName: f.seniorName.trim(), seniorEmail: trim(f.seniorEmail), familyName: trim(f.familyName), familyEmail: trim(f.familyEmail) },
      });
      setMade({ url: r.inviteUrl ?? null, seniorUrl: r.seniorInviteUrl ?? null });
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  if (!open)
    return (
      <a href="#" {...wr(() => { setOpen(true); setMade(null); })} style={sx(btn('ghost'))}>
        + Aithora Home household
      </a>
    );
  return (
    <div data-testid="household-create" style={sx('flex-basis:100%;display:flex;flex-direction:column;gap:8px;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
      {made ? (
        <>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(f.householdName.trim() + ' is set up.')}</div>
          {made.seniorUrl && (
            <div data-testid="senior-invite" style={sx('font-size:16px;color:#54606C;')}>
              {I(f.seniorName.trim() + "'s own sign-in link (expires in 7 days): ")}
              <code style={sx('user-select:all;')}>{made.seniorUrl}</code>
            </div>
          )}
          {made.url && (
            <div data-testid="family-invite" style={sx('font-size:16px;color:#54606C;')}>
              {I('Family invite link (expires in 7 days): ')}
              <code style={sx('user-select:all;')}>{made.url}</code>
            </div>
          )}
          <a href="#" onClick={(e) => { pre(e); setOpen(false); setF({ householdName: '', seniorName: '', seniorEmail: '', familyName: '', familyEmail: '' }); }} style={sx(btn('ghost'))}>
            Done
          </a>
        </>
      ) : (
        <>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            <input type="text" aria-label="Household name" placeholder="Household name" value={f.householdName} onChange={set('householdName')} style={sx(INPUT)} />
            <input type="text" aria-label="Senior's name" placeholder="The senior's name" value={f.seniorName} onChange={set('seniorName')} style={sx(INPUT)} />
            <input type="email" aria-label="Senior's email" placeholder="Their email (optional)" value={f.seniorEmail} onChange={set('seniorEmail')} style={sx(INPUT)} />
          </div>
          <div style={sx('display:flex;gap:8px;flex-wrap:wrap;')}>
            <input type="text" aria-label="Family member's name" placeholder="A family member (optional)" value={f.familyName} onChange={set('familyName')} style={sx(INPUT)} />
            <input type="email" aria-label="Family member's email" placeholder="Their email, for the invite" value={f.familyEmail} onChange={set('familyEmail')} style={sx(INPUT)} />
          </div>
          <div style={sx('display:flex;gap:8px;')}>
            <a href="#" {...wr(() => void create())} aria-disabled={!ok || undefined} style={sx(btn(ok ? 'primary' : 'off'))}>
              Create household
            </a>
            <a href="#" onClick={(e) => { pre(e); setOpen(false); }} style={sx(btn('ghost'))}>
              Cancel
            </a>
          </div>
        </>
      )}
      <ErrLine error={err} />
    </div>
  );
}
